// Shared state for the Flight Breakdown page's view mode so the toggle in the
// NavBar (shown only on /flight-data) and the page itself stay in sync.
//   'classic' = map/tabs/legend stacked above the board (original layout)
//   'focus'   = board-first, with that content in a pull-down drawer
// Persisted per dispatcher in localStorage.
import React, { createContext, useContext, useState, useCallback } from 'react';

const BreakdownViewContext = createContext(null);

const STORAGE_KEY = 'breakdownViewMode';

export function BreakdownViewProvider({ children }) {
  const [viewMode, setViewModeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'focus' ? 'focus' : 'classic';
    } catch {
      return 'classic';
    }
  });

  // Focus-mode details drawer. Lives here (not in the page) because the
  // DETAILS button renders in the NavBar next to the Classic/Focus toggle.
  // Not persisted — a fresh visit starts with the drawer closed.
  const [detailsOpen, setDetailsOpen] = useState(false);

  const setViewMode = useCallback((mode) => {
    if (mode !== 'classic' && mode !== 'focus') return; // ignore null/unknown
    setViewModeState(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
  }, []);

  return (
    <BreakdownViewContext.Provider value={{ viewMode, setViewMode, detailsOpen, setDetailsOpen }}>
      {children}
    </BreakdownViewContext.Provider>
  );
}

export function useBreakdownView() {
  const ctx = useContext(BreakdownViewContext);
  // Fallback keeps any consumer rendered outside the provider from crashing.
  return ctx || {
    viewMode: 'classic', setViewMode: () => {},
    detailsOpen: false, setDetailsOpen: () => {},
  };
}
